import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText, Package, Save, Briefcase, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OpportunityDetail = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opp, setOpp] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadOpp = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/opportunities/${opportunityId}`, { withCredentials: true });
      setOpp(res.data);
      const ansMap = {};
      (res.data.answers || []).forEach(a => { ansMap[a.question_id] = a.value || ''; });
      setAnswers(ansMap);
    } catch (error) {
      toast.error('Erro ao carregar oportunidade');
      navigate('/opportunities');
    } finally {
      setLoading(false);
    }
  }, [opportunityId, navigate]);

  useEffect(() => { loadOpp(); }, [loadOpp]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const answersList = Object.entries(answers).map(([question_id, value]) => ({ question_id, value }));
      await axios.put(`${API_URL}/opportunities/${opportunityId}`, { answers: answersList }, { withCredentials: true });
      toast.success('Respostas salvas');
      loadOpp();
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/opportunities/${opportunityId}/export?format=${format}`, {
        method: 'POST', credentials: 'include'
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagnostico_${opp.name}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado como ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Erro ao exportar ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin" /></div>;
  if (!opp) return null;

  // Group answers by group
  const groupedAnswers = {};
  (opp.answers || []).forEach(a => {
    const g = a.group || 'Geral';
    if (!groupedAnswers[g]) groupedAnswers[g] = [];
    groupedAnswers[g].push(a);
  });

  return (
    <div className="space-y-6" data-testid="opportunity-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/opportunities')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#111111]">{opp.name}</h1>
            <p className="text-sm text-[#6E6E73] mt-0.5">{opp.client_name} · {opp.tipo_documento}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleExport('xlsx')} disabled={exporting} data-testid="export-xlsx-btn">
            <Download className="w-4 h-4 mr-2" /> XLSX
          </Button>
          <Button variant="outline" onClick={() => handleExport('docx')} disabled={exporting} data-testid="export-docx-btn">
            <Download className="w-4 h-4 mr-2" /> DOCX
          </Button>
          <Button variant="outline" onClick={() => handleExport('pdf')} disabled={exporting} data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="save-answers-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar Respostas'}
          </Button>
          {(user?.role === 'Administrador' || user?.role === 'Redator Sênior') && (
            <Button variant="outline" className="border-red-300 text-red-500 hover:bg-red-50" data-testid="delete-opportunity-detail-btn"
              onClick={async () => {
                if (!window.confirm(`Excluir o perfil "${opp.name}"?`)) return;
                try {
                  await axios.delete(`${API_URL}/opportunities/${opportunityId}`);
                  toast.success('Perfil excluído');
                  navigate('/opportunities');
                } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao excluir'); }
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Excluir
            </Button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="w-4 h-4 text-[#AF2628]" />
            <span className="text-xs font-medium text-[#6E6E73]">Informações</span>
          </div>
          <p className="text-sm"><strong>Tipo:</strong> {opp.tipo_documento}</p>
          {opp.numero_processo && <p className="text-sm mt-1"><strong>Processo:</strong> {opp.numero_processo}</p>}
          {opp.descricao && <p className="text-sm mt-1 text-[#6E6E73]">{opp.descricao}</p>}
        </div>
        <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-4 h-4 text-[#AF2628]" />
            <span className="text-xs font-medium text-[#6E6E73]">Produtos ({opp.products?.length || 0})</span>
          </div>
          <div className="space-y-1">
            {opp.products?.map(p => (
              <p key={p.product_id} className="text-sm">{p.name}</p>
            ))}
            {(!opp.products || opp.products.length === 0) && <p className="text-sm text-[#AEAEB2]">Nenhum produto</p>}
          </div>
        </div>
        <div className="bg-white border border-[#E5E5EA] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-[#AF2628]" />
            <span className="text-xs font-medium text-[#6E6E73]">Documentos Gerados</span>
          </div>
          {opp.documents_data?.length > 0 ? (
            opp.documents_data.map(doc => (
              <button key={doc.document_id} onClick={() => navigate(`/documents/${doc.document_id}`)} className="text-sm text-[#AF2628] hover:underline block">
                {doc.title}
              </button>
            ))
          ) : (
            <p className="text-sm text-[#AEAEB2]">Nenhum documento gerado</p>
          )}
        </div>
      </div>

      {/* Questionnaire */}
      <div className="bg-white border border-[#E5E5EA] rounded-xl">
        <div className="p-4 border-b border-[#E5E5EA]">
          <h2 className="font-semibold text-[#111111]">Questionário de Diagnóstico</h2>
          <p className="text-xs text-[#6E6E73] mt-0.5">{opp.answers?.length || 0} pergunta(s)</p>
        </div>
        <div className="p-4 space-y-6">
          {Object.entries(groupedAnswers).map(([groupName, groupAnswers]) => (
            <div key={groupName}>
              <h3 className="text-sm font-semibold text-[#AF2628] border-b border-[#E5E5EA] pb-2 mb-3">{groupName}</h3>
              <div className="space-y-4">
                {groupAnswers.map(a => (
                  <div key={a.question_id} data-testid={`answer-${a.question_id}`}>
                    <Label className="text-sm">
                      {a.question_text}
                      {a.variable_name && (
                        <span className="ml-2 text-[10px] font-mono bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded">
                          {'{{' + a.variable_name + '}}'}
                        </span>
                      )}
                    </Label>
                    <Input
                      value={answers[a.question_id] || ''}
                      onChange={e => setAnswers(prev => ({ ...prev, [a.question_id]: e.target.value }))}
                      className="mt-1"
                      placeholder="Digite a resposta..."
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {Object.keys(groupedAnswers).length === 0 && (
            <p className="text-sm text-[#AEAEB2] text-center py-6">Nenhuma pergunta vinculada a esta oportunidade</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpportunityDetail;
